import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Code,
  IconButton,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
  Badge,
  Flex,
} from "@chakra-ui/react";
import { ArrowLeftIcon, ArrowRightIcon, DownloadIcon } from "@chakra-ui/icons";

const DiffModal = ({
  isOpen,
  onClose,
  diffContent,
  fromVersion,
  toVersion,
  changePositions = [],
  onExportDiff,
  isAdmin = false,
}) => {
  const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const diffContainerRef = useRef(null);
  const toast = useToast();

  // Reset navigation when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentChangeIndex(0);
      setIsNavigating(false);

      // Focus the diff container to enable scroll wheel
      setTimeout(() => {
        if (diffContainerRef.current) {
          // Set up the container for proper scrolling
          diffContainerRef.current.setAttribute("tabindex", "0");
          diffContainerRef.current.style.pointerEvents = "auto";
          diffContainerRef.current.style.overflowY = "auto";
          diffContainerRef.current.focus();

          // Add a wheel event listener directly to the element
          const handleWheel = (e) => {
            e.stopPropagation();
          };
          diffContainerRef.current.addEventListener("wheel", handleWheel, {
            passive: true,
          });

          // Store the handler for cleanup
          diffContainerRef.current._wheelHandler = handleWheel;
        }
      }, 200);
    }
  }, [isOpen]);

  // Add keyboard navigation and cleanup
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!isOpen) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        navigateToPreviousChange();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        navigateToNextChange();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyPress);
      return () => {
        document.removeEventListener("keydown", handleKeyPress);
        // Clean up wheel event listener
        if (
          diffContainerRef.current &&
          diffContainerRef.current._wheelHandler
        ) {
          diffContainerRef.current.removeEventListener(
            "wheel",
            diffContainerRef.current._wheelHandler
          );
          delete diffContainerRef.current._wheelHandler;
        }
      };
    }
  }, [isOpen, currentChangeIndex, changePositions.length, isNavigating]);

  // Navigate to next change
  const navigateToNextChange = () => {
    if (changePositions.length === 0 || isNavigating) return;
    const nextIndex = (currentChangeIndex + 1) % changePositions.length;
    setCurrentChangeIndex(nextIndex);
    scrollToChange(nextIndex);
  };

  // Navigate to previous change
  const navigateToPreviousChange = () => {
    if (changePositions.length === 0 || isNavigating) return;
    const prevIndex =
      currentChangeIndex === 0
        ? changePositions.length - 1
        : currentChangeIndex - 1;
    setCurrentChangeIndex(prevIndex);
    scrollToChange(prevIndex);
  };

  // Scroll to a specific change
  const scrollToChange = (changeIndex) => {
    const changePosition = changePositions[changeIndex];
    if (!changePosition) return;

    setIsNavigating(true);

    // Find the element with the change
    const changeElements = document.querySelectorAll("[data-diff-line]");
    const targetElement = changeElements[changePosition.index];

    if (targetElement) {
      // Use the ref for the diff container
      const diffContainer = diffContainerRef.current;
      if (diffContainer) {
        // Focus the container to ensure scroll events work
        diffContainer.focus();

        // Calculate the target scroll position
        const containerRect = diffContainer.getBoundingClientRect();
        const elementRect = targetElement.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top;

        // Calculate target scroll position - center the element in the visible area
        const containerHeight = diffContainer.clientHeight;
        const elementHeight = targetElement.offsetHeight;
        const stickyNavHeight = 100; // Height of sticky navigation
        const visibleAreaHeight = containerHeight - stickyNavHeight;

        // Center the element in the visible area
        const targetScrollTop = Math.max(
          0,
          diffContainer.scrollTop +
            relativeTop -
            visibleAreaHeight / 2 +
            elementHeight / 2
        );

        // Smooth scroll to the target position
        diffContainer.scrollTo({
          top: targetScrollTop,
          behavior: "smooth",
        });
      } else {
        // Fallback to original behavior
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      // Add highlight effect with color based on change type
      const highlightColor =
        changePosition.type === "added" ? "#22C55E" : "#EF4444";
      targetElement.style.backgroundColor = highlightColor;
      targetElement.style.transition = "background-color 0.2s ease";

      // Reduce timeout for faster navigation
      setTimeout(() => {
        targetElement.style.backgroundColor = "";
        setIsNavigating(false);
      }, 300);
    } else {
      setIsNavigating(false);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Get version number from version object
  const getVersionNumber = (version) => {
    if (!version) return "Unknown";
    return version.versionNumber || "Unknown";
  };

  if (!diffContent) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent
        maxH="90vh"
        maxW="95vw"
        display="flex"
        flexDirection="column"
      >
        <ModalHeader
          flexShrink={0}
          bg="blue.50"
          borderBottom="1px solid"
          borderColor="blue.200"
        >
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text fontSize="xl" fontWeight="bold" color="blue.700">
                Document Diff Comparison
              </Text>
              <HStack spacing={2}>
                {isAdmin && onExportDiff && (
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    leftIcon={<DownloadIcon />}
                    onClick={onExportDiff}
                  >
                    Export JSON
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={onClose}>
                  Close
                </Button>
              </HStack>
            </HStack>

            {/* Version Information */}
            <HStack spacing={6} justify="center">
              <Box textAlign="center">
                <Text fontSize="sm" fontWeight="medium" color="blue.600">
                  Older Version
                </Text>
                <Text fontSize="sm" color="gray.700">
                  Version {getVersionNumber(fromVersion)}
                </Text>
                {fromVersion?.timestamp && (
                  <Text fontSize="xs" color="gray.500">
                    {formatTimestamp(fromVersion.timestamp)}
                  </Text>
                )}
                {fromVersion?.user && (
                  <Text fontSize="xs" color="gray.500">
                    by {fromVersion.user}
                  </Text>
                )}
              </Box>

              <Box textAlign="center">
                <Text fontSize="sm" fontWeight="medium" color="green.600">
                  Newer Version
                </Text>
                <Text fontSize="sm" color="gray.700">
                  Version {getVersionNumber(toVersion)}
                </Text>
                {toVersion?.timestamp && (
                  <Text fontSize="xs" color="gray.500">
                    {formatTimestamp(toVersion.timestamp)}
                  </Text>
                )}
                {toVersion?.user && (
                  <Text fontSize="xs" color="gray.500">
                    by {toVersion.user}
                  </Text>
                )}
              </Box>
            </HStack>
          </VStack>
        </ModalHeader>

        <ModalBody flex="1" overflow="hidden" p={0}>
          <VStack spacing={0} align="stretch" h="100%" overflow="hidden">
            {/* Diff Summary */}
            <Box
              p={4}
              bg="gray.50"
              borderBottom="1px solid"
              borderColor="gray.200"
            >
              <HStack spacing={6} justify="center">
                <HStack spacing={2}>
                  <Box w={4} h={4} bg="green.100" borderRadius="sm" />
                  <Text fontSize="sm" color="green.700" fontWeight="medium">
                    +
                    {diffContent.filter((line) => line.type === "added").length}{" "}
                    added
                  </Text>
                </HStack>
                <HStack spacing={2}>
                  <Box w={4} h={4} bg="red.100" borderRadius="sm" />
                  <Text fontSize="sm" color="red.700" fontWeight="medium">
                    -
                    {
                      diffContent.filter((line) => line.type === "removed")
                        .length
                    }{" "}
                    removed
                  </Text>
                </HStack>
                <HStack spacing={2}>
                  <Box w={4} h={4} bg="gray.100" borderRadius="sm" />
                  <Text fontSize="sm" color="gray.700" fontWeight="medium">
                    {
                      diffContent.filter((line) => line.type === "unchanged")
                        .length
                    }{" "}
                    unchanged
                  </Text>
                </HStack>
              </HStack>
            </Box>

            {/* Navigation Controls - Sticky */}
            {changePositions.length > 0 && (
              <Box
                position="sticky"
                top="0"
                zIndex={10}
                bg="white"
                p={3}
                borderBottom="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
              >
                <HStack justify="center" spacing={4}>
                  <IconButton
                    size="md"
                    icon={<ArrowLeftIcon />}
                    onClick={navigateToPreviousChange}
                    aria-label="Previous change"
                    title={`Previous change (${currentChangeIndex + 1}/${
                      changePositions.length
                    }) - Use ← arrow key`}
                    isDisabled={changePositions.length === 0}
                    colorScheme="blue"
                    variant="solid"
                  />
                  <Text
                    fontSize="md"
                    color="gray.700"
                    minW="100px"
                    textAlign="center"
                    fontWeight="bold"
                  >
                    {changePositions.length > 0
                      ? `${currentChangeIndex + 1} of ${changePositions.length}`
                      : "0 of 0"}
                  </Text>
                  <IconButton
                    size="md"
                    icon={<ArrowRightIcon />}
                    onClick={navigateToNextChange}
                    aria-label="Next change"
                    title={`Next change (${currentChangeIndex + 1}/${
                      changePositions.length
                    }) - Use → arrow key`}
                    isDisabled={changePositions.length === 0}
                    colorScheme="blue"
                    variant="solid"
                  />
                </HStack>
              </Box>
            )}

            {/* Diff Content */}
            <Box
              ref={diffContainerRef}
              flex="1"
              overflowY="auto"
              bg="white"
              data-diff-container
              minH="0"
              maxH="calc(90vh - 200px)"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              mx={4}
              mb={4}
              position="relative"
              tabIndex={-1}
              _focus={{ outline: "2px solid", outlineColor: "blue.500" }}
              onMouseEnter={() => {
                if (diffContainerRef.current) {
                  diffContainerRef.current.focus();
                }
              }}
              onWheel={(e) => {
                // Handle wheel events directly
                if (diffContainerRef.current) {
                  diffContainerRef.current.focus();
                  // Allow the default scroll behavior
                  e.stopPropagation();
                }
              }}
              css={{
                "&::-webkit-scrollbar": {
                  width: "12px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "6px",
                  border: "2px solid #f1f1f1",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#a8a8a8",
                },
                "&:focus": {
                  outline: "2px solid #3182ce",
                  outlineOffset: "2px",
                },
              }}
            >
              {/* Scroll indicator */}
              <Box
                position="absolute"
                top={2}
                right={4}
                bg="blue.100"
                color="blue.700"
                px={2}
                py={1}
                borderRadius="sm"
                fontSize="xs"
                fontWeight="medium"
                zIndex={5}
                pointerEvents="none"
              >
                Hover and scroll or use ← → keys
              </Box>
              <Code
                display="block"
                whiteSpace="pre-wrap"
                fontSize="sm"
                p={4}
                className="diff-content-code"
              >
                {diffContent.map((line, index) => {
                  if (line.type === "added") {
                    return (
                      <Box
                        key={index}
                        data-diff-line={index}
                        data-diff-type="added"
                        className="diff-line-added"
                        style={{
                          backgroundColor: "#dcfce7",
                          color: "#166534",
                          borderLeft: "4px solid #22c55e",
                          padding: "8px",
                          marginBottom: "4px",
                          borderRadius: "4px",
                          transition: "background-color 0.3s ease",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        <Text as="span" color="#166534" fontWeight="medium">
                          + {line.content}
                        </Text>
                      </Box>
                    );
                  } else if (line.type === "removed") {
                    return (
                      <Box
                        key={index}
                        data-diff-line={index}
                        data-diff-type="removed"
                        className="diff-line-removed"
                        style={{
                          backgroundColor: "#fef2f2",
                          color: "#991b1b",
                          borderLeft: "4px solid #ef4444",
                          padding: "8px",
                          marginBottom: "4px",
                          borderRadius: "4px",
                          transition: "background-color 0.3s ease",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        <Text as="span" color="#991b1b" fontWeight="medium">
                          - {line.content}
                        </Text>
                      </Box>
                    );
                  } else {
                    return (
                      <Box
                        key={index}
                        data-diff-line={index}
                        data-diff-type="unchanged"
                        className="diff-line-unchanged"
                        style={{
                          backgroundColor: "#f3f4f6",
                          padding: "8px",
                          marginBottom: "4px",
                          transition: "background-color 0.3s ease",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                        _hover={{ backgroundColor: "#e5e7eb" }}
                      >
                        <Text as="span" color="#6b7280">
                          {line.content}
                        </Text>
                      </Box>
                    );
                  }
                })}
              </Code>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DiffModal;
